import { type CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { getSVGIconNameFromDocument } from '../../utils/getSVGIconNameFromDocument';
import { Button } from '../buttons/Button';
import { SVGIcon } from '../icons/SVGIcon';
import { PdfDocumentPreview } from '../pdf-document-preview/PdfDocumentPreview';

import {
  DocumentPreviewMetadataSection,
  DocumentPreviewMetadataToggle,
} from './DocumentPreviewMetadataSection';
import { createProvenancePageLayer } from './provenance';
import type { AiAssistantPreviewDocument } from './types';
import { useDocumentPreviewMetadataSection } from './useDocumentPreviewMetadataSection';
import { useRetainedPanelContent } from './useRetainedPanelContent';

import styles from './DocumentPreviewPanel.module.scss';

export type DocumentPreviewPanelProps = {
  document: AiAssistantPreviewDocument | null;
  rightOffset: number;
  onClose: () => void;
};

export function DocumentPreviewPanel({
  document,
  rightOffset,
  onClose,
}: DocumentPreviewPanelProps) {
  const { t } = useTranslation();
  const {
    active: activeDocument,
    handleTransitionEnd,
    isOpen,
  } = useRetainedPanelContent(document);
  const documentPreviewMetadata = useDocumentPreviewMetadataSection({
    metadata: activeDocument?.metadata,
    resetWhen: document,
  });

  const provenanceLayer = useMemo(
    () => createProvenancePageLayer(activeDocument?.provenance),
    [activeDocument?.provenance]
  );
  const initialPage =
    activeDocument?.provenance?.[0]?.page_no ?? activeDocument?.pages?.[0] ?? 1;

  return (
    <aside
      aria-hidden={!isOpen}
      aria-label={t('app.pdfDocumentPreview.title')}
      className={clsx(styles['panel'], {
        [styles['panel--open']]: isOpen,
      })}
      data-testid="ai-assistant-document-preview"
      onTransitionEnd={handleTransitionEnd}
      role="dialog"
      style={{ right: `${rightOffset}px` } as CSSProperties}
    >
      <header className={styles['header']}>
        <div className={styles['header-left']}>
          <span className={styles['title']}>
            <SVGIcon
              name={getSVGIconNameFromDocument({
                fileName: activeDocument?.label ?? '',
              })}
            />
            <span className={styles['title-text']}>
              {activeDocument?.label}
            </span>
          </span>
          {documentPreviewMetadata.hasEntries ? (
            <DocumentPreviewMetadataToggle
              isOpen={documentPreviewMetadata.isOpen}
              onToggle={documentPreviewMetadata.toggle}
            />
          ) : null}
        </div>
        <Button
          data-testid="ai-assistant-preview-close"
          icon={<SVGIcon name="close" />}
          onClick={onClose}
          title={t('app.pdfDocumentPreview.close')}
          variant="text"
        />
      </header>
      <DocumentPreviewMetadataSection
        entries={documentPreviewMetadata.entries}
        isOpen={documentPreviewMetadata.isOpen}
      />
      <div className={styles['preview']}>
        {activeDocument ? (
          <PdfDocumentPreview
            ariaLabel={t('app.pdfDocumentPreview.title')}
            filename={activeDocument.label}
            initialPage={initialPage}
            markedPages={activeDocument.pages}
            pageLayers={[provenanceLayer]}
            source={{ type: 'url', url: activeDocument.file }}
          />
        ) : null}
      </div>
    </aside>
  );
}
